import { IAppState } from '@store/reducers';
import { MUTUALLY_EXCLUSIVE_UPSELLS } from '@store/reducers/upsell';
import { IUpsell, UpsellType } from '@types';
import { createSelector } from 'reselect';

const compensationOptions = (state: IAppState): IUpsell[] => {
    if (!state.upsell.options) {
        return [];
    }

    return state.upsell.options.filter((option: IUpsell): boolean => {
        return MUTUALLY_EXCLUSIVE_UPSELLS.includes(option.type);
    });
};

/**
 * Computes the selected upsells that are a consequence of selecting another and do not appear in the UI.
 * I.e. If the user selects ENHANCED implies the upsell PROOF_OF_DELIVERY will be included/selected if the option is
 * available in the desired service.
 */
const computedSelectedUpsells = (state: IAppState): UpsellType[] => {
    const { options, selected } = state.upsell;
    const computedSelected = [...selected];

    if (options.length === 0 || selected.length === 0) {
        return [];
    }

    const hasProofOfDelivery = options.some((item: IUpsell): boolean => item.type === UpsellType.PROOF_OF_DELIVERY);
    const proofOfDelivery =
        (selected.includes(UpsellType.PROOF_OF_DELIVERY) || selected.includes(UpsellType.ENHANCED)) &&
        hasProofOfDelivery;

    if (!computedSelected.includes(UpsellType.PROOF_OF_DELIVERY) && proofOfDelivery) {
        computedSelected.push(UpsellType.PROOF_OF_DELIVERY);
    }

    return computedSelected;
};

const selectedUpsellsCoverage = (state: IAppState): number => {
    const { options, selected } = state.upsell;

    if (options.length === 0 || selected.length === 0) {
        return 0;
    }

    const enhancedSelected = selected.includes(UpsellType.ENHANCED);
    if (enhancedSelected) {
        const enhanced = options.find((item: IUpsell): boolean => item.type === UpsellType.ENHANCED);
        return enhanced ? enhanced.coverage : 0;
    }

    const proofOfDeliverySelected = selected.includes(UpsellType.PROOF_OF_DELIVERY);
    if (proofOfDeliverySelected) {
        const proofOfDelivery = options.find((item: IUpsell): boolean => item.type === UpsellType.PROOF_OF_DELIVERY);
        return proofOfDelivery ? proofOfDelivery.coverage : 0;
    }

    const standardSelected = selected.includes(UpsellType.STANDARD);
    if (standardSelected) {
        const standard = options.find((item: IUpsell): boolean => item.type === UpsellType.STANDARD);
        return standard ? standard.coverage : 0;
    }

    return 0;
};

export const compensationOptionsState = createSelector(
    [compensationOptions],
    (options: IUpsell[]): IUpsell[] => options,
);

export const computedSelectedUpsellsState = createSelector(
    [computedSelectedUpsells],
    (selectedUpsells: UpsellType[]): UpsellType[] => selectedUpsells,
);

export const selectedUpsellsCoverageState = createSelector(
    [selectedUpsellsCoverage],
    (coverage: number): number => coverage,
);

export const getUpsellOptions = createSelector(
    [(state: IAppState) => state.upsell.options],
    (options: IUpsell[]) => options,
);
